/**
 * Population health Score Method — top of Manage MVPs (same shell as Cost / IA / PI).
 * Estimate edits auto-save (debounced). Score Method persists in mvp_practice_prefs (DB).
 * No empty→loaded toggle flash: wait until queries are fetched, then draft only while editing.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { keepPreviousSameEntity } from '@/lib/query-keys';
import { extractPopHealth } from '@/lib/mvp/assemble-inputs';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/components/ui/use-toast';
import { Activity, Circle, CircleDot, HelpCircle } from 'lucide-react';

type ScoreMethod = 'submission' | 'estimate';

type MeasureForm = { points: string; caseMin: boolean };
type PopForm = Record<'479' | '484', MeasureForm>;

/** CMS admin-claims case minimums (MVP population health — QPP webinar / measure specs). */
const CASE_MIN: Record<'479' | '484', { cases: number; unit: string; clinicianFloor: number }> = {
  '479': { cases: 200, unit: 'eligible index admissions', clinicianFloor: 16 },
  '484': { cases: 18, unit: 'attributed patients with MCCs', clinicianFloor: 16 },
};

function fmtPts(v: number | null | undefined) {
  if (v == null || Number.isNaN(v)) return '—';
  return Number.isInteger(v) ? String(v) : v.toFixed(1);
}

function formFromEntries(entries: any[] | undefined): PopForm {
  const next: PopForm = {
    '479': { points: '', caseMin: false },
    '484': { points: '', caseMin: false },
  };
  for (const e of entries ?? []) {
    const id = String(e.measure_id) as '479' | '484';
    if (next[id]) {
      next[id] = {
        points: e.score_points != null ? String(e.score_points) : '',
        caseMin: !!e.case_min_met,
      };
    }
  }
  return next;
}

function formsEqual(a: PopForm, b: PopForm) {
  return (['479', '484'] as const).every(
    id => a[id].points === b[id].points && a[id].caseMin === b[id].caseMin,
  );
}

function LabelWithTip({ children, tip }: { children: React.ReactNode; tip: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      {children}
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="text-muted-foreground hover:text-foreground">
            <HelpCircle className="h-3.5 w-3.5" />
          </button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs text-xs leading-relaxed">{tip}</TooltipContent>
      </Tooltip>
    </span>
  );
}

export function PopHealthCard({ practiceId, programYear }: {
  practiceId: string | number | null; programYear: number;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const submissionQ = useQuery<any>({
    queryKey: ['mvp', 'score-submission', practiceId ?? '', programYear],
    queryFn: () => apiClient.get(`/api/practices/${practiceId}/score-submissions/${programYear}`).catch(() => null),
    enabled: !!practiceId,
    placeholderData: keepPreviousSameEntity(2, String(practiceId ?? '')),
    staleTime: 60_000,
  });
  const manualQ = useQuery<any[]>({
    queryKey: ['mvp', 'pop-health', practiceId ?? '', programYear],
    queryFn: () => apiClient.get(`/api/mvp/${practiceId}/pop-health?year=${programYear}`).catch(() => []),
    enabled: !!practiceId,
    placeholderData: keepPreviousSameEntity<any[]>(2, String(practiceId ?? '')),
    staleTime: 60_000,
  });
  const methodQ = useQuery<{ method: ScoreMethod | null }>({
    queryKey: ['mvp', 'pop-health-method', practiceId ?? '', programYear],
    queryFn: () => apiClient.get(`/api/mvp/${practiceId}/pop-health-method?year=${programYear}`),
    enabled: !!practiceId,
    placeholderData: keepPreviousSameEntity(2, String(practiceId ?? '')),
    staleTime: 60_000,
  });

  const serverForm = useMemo(() => formFromEntries(manualQ.data), [manualQ.data]);
  // Local edits only — never paint EMPTY_FORM while waiting on the server
  const [draft, setDraft] = useState<PopForm | null>(null);
  const form = draft ?? serverForm;
  const [savingHint, setSavingHint] = useState(false);
  const [methodSaving, setMethodSaving] = useState(false);

  // Practice/year change: drop draft so we rehydrate from server
  useEffect(() => {
    setDraft(null);
  }, [practiceId, programYear]);

  // After a successful save, server catches up to draft → drop draft (no visual jump)
  useEffect(() => {
    if (draft && formsEqual(draft, serverForm)) setDraft(null);
  }, [serverForm, draft]);

  const effective = useMemo(
    () => extractPopHealth(submissionQ.data, manualQ.data ?? []),
    [submissionQ.data, manualQ.data],
  );

  const fileHasAny = effective.m479?.source === 'score_submission' || effective.m484?.source === 'score_submission';
  const savedMethod = methodQ.data?.method ?? null;
  const activeMethod: ScoreMethod = savedMethod ?? (fileHasAny ? 'submission' : 'estimate');

  const ready = !!practiceId && submissionQ.isFetched && manualQ.isFetched && methodQ.isFetched;

  const chooseMethod = async (method: ScoreMethod) => {
    if (!practiceId || method === activeMethod || methodSaving) return;
    // Optimistic cache update (same pattern as Cost modeled toggles)
    queryClient.setQueryData(['mvp', 'pop-health-method', practiceId, programYear], { method });
    setMethodSaving(true);
    try {
      await apiClient.put(`/api/mvp/${practiceId}/pop-health-method?year=${programYear}`, { method });
    } catch (e: any) {
      await queryClient.invalidateQueries({ queryKey: ['mvp', 'pop-health-method', practiceId, programYear] });
      toast({ title: 'Failed to save score method', description: e?.message ?? '', variant: 'destructive' });
    } finally {
      setMethodSaving(false);
    }
  };

  // Debounced auto-save from draft only
  useEffect(() => {
    if (!practiceId || !ready || activeMethod !== 'estimate' || !draft) return;
    if (formsEqual(draft, serverForm)) return;

    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSavingHint(true);
      try {
        const entries = (['479', '484'] as const).map(id => ({
          measure_id: id,
          score_points: draft[id].points === '' ? null : Number(draft[id].points),
          case_min_met: draft[id].caseMin,
        }));
        await apiClient.put(`/api/mvp/${practiceId}/pop-health?year=${programYear}`, { entries });
        await queryClient.invalidateQueries({ queryKey: ['mvp', 'pop-health', practiceId, programYear] });
      } catch (e: any) {
        toast({ title: 'Failed to save estimate', description: e?.message ?? '', variant: 'destructive' });
      } finally {
        setSavingHint(false);
      }
    }, 450);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [draft, practiceId, programYear, activeMethod, ready, serverForm, queryClient, toast]);

  if (!practiceId) return null;

  if (!ready) {
    return (
      <Card>
        <CardContent className="py-5 px-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6 min-h-[128px]">
            <div className="lg:pr-6 lg:border-r border-gray-200 shrink-0 space-y-2 w-full lg:w-[220px]">
              <div className="h-4 w-24 rounded bg-muted animate-pulse" />
              <div className="h-9 w-full rounded bg-muted/70 animate-pulse" />
              <div className="h-9 w-full rounded bg-muted/70 animate-pulse" />
            </div>
            <div className="flex-1 space-y-2">
              <div className="h-5 w-48 rounded bg-muted animate-pulse" />
              <div className="h-4 w-full max-w-md rounded bg-muted/60 animate-pulse" />
              <div className="h-4 w-2/3 max-w-sm rounded bg-muted/60 animate-pulse" />
            </div>
            <div className="lg:pl-6 lg:border-l border-gray-200 shrink-0 space-y-2 w-[100px]">
              <div className="h-4 w-16 mx-auto rounded bg-muted animate-pulse" />
              <div className="h-8 w-14 mx-auto rounded bg-muted/70 animate-pulse" />
              <div className="h-8 w-14 mx-auto rounded bg-muted/70 animate-pulse" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const sourceLabel = fileHasAny
    ? 'Score submission'
    : (effective.m479?.source === 'manual' || effective.m484?.source === 'manual')
      ? 'Estimate'
      : 'Not set';

  const updateMeasure = (id: '479' | '484', patch: Partial<MeasureForm>) => {
    setDraft(prev => {
      const base = prev ?? serverForm;
      return { ...base, [id]: { ...base[id], ...patch } };
    });
  };

  return (
    <TooltipProvider delayDuration={200}>
      <Card>
        <CardContent className="py-5 px-6">
          <div className="flex flex-col lg:flex-row lg:items-start gap-6">
            <div className="flex flex-col lg:pr-6 lg:border-r border-gray-200 shrink-0">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Score Method</h4>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => chooseMethod('submission')}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium border transition-colors w-full ${
                    activeMethod === 'submission'
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {activeMethod === 'submission' ? <CircleDot className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                  From score submission
                </button>
                <button
                  type="button"
                  onClick={() => chooseMethod('estimate')}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium border transition-colors w-full ${
                    activeMethod === 'estimate'
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {activeMethod === 'estimate' ? <CircleDot className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
                  Enter estimate
                </button>
              </div>
            </div>

            {/* min-h matches estimate form so toggle doesn’t resize the card */}
            <div className="flex-1 min-w-0 lg:px-2 min-h-[168px]">
              {activeMethod === 'submission' ? (
                <>
                  <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Activity className="w-5 h-5 text-primary" />
                    Population Health Preview
                  </h3>
                  <p className="text-sm text-gray-700 mt-1 leading-relaxed">
                    479 and 484 are the MVP <strong className="text-gray-900">foundation</strong> quality layer.
                    They feed pathway projections when scoreable.
                  </p>
                  <p className="text-sm text-amber-800 mt-2.5 leading-relaxed">
                    {fileHasAny ? (
                      <>Using points from your <strong>score submission</strong> Administrative Claims rows.</>
                    ) : (
                      <>No 479/484 in the score submission yet — switch to <strong>Enter estimate</strong>, or upload a file under QPP Reports.</>
                    )}
                  </p>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                      <Activity className="w-5 h-5 text-primary" />
                      Enter estimate
                    </h3>
                    {savingHint && (
                      <span className="text-xs text-muted-foreground">Saving…</span>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {(['479', '484'] as const).map(id => {
                      const fromFile = effective[`m${id}`]?.source === 'score_submission';
                      const title = id === '479' ? '479 — HWR' : '484 — MCC';
                      const cm = CASE_MIN[id];
                      return (
                        <div key={id} className="rounded-md border border-gray-200 px-3 py-2.5 space-y-2.5">
                          <div className="text-sm font-medium text-gray-800">{title}</div>
                          {fromFile ? (
                            <p className="text-xs text-muted-foreground">
                              {fmtPts(effective[`m${id}`]!.points)} pts from score submission (wins)
                            </p>
                          ) : (
                            <>
                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground font-normal">
                                  <LabelWithTip tip="CMS assigns a decile score from 0–10 for this administrative-claims measure. Enter the estimated achievement points you expect for projections when you don’t have a score submission row yet.">
                                    Estimated decile points (0–10)
                                  </LabelWithTip>
                                </Label>
                                <Input
                                  type="number" min={0} max={10} step={0.1}
                                  className="w-[5.5rem] h-8"
                                  placeholder="—"
                                  value={form[id].points}
                                  onChange={e => updateMeasure(id, { points: e.target.value })}
                                />
                              </div>
                              <div className="flex items-center gap-2">
                                <Switch
                                  id={`ph-cm-${id}`}
                                  checked={form[id].caseMin}
                                  onCheckedChange={v => updateMeasure(id, { caseMin: v })}
                                />
                                <Label htmlFor={`ph-cm-${id}`} className="text-xs text-muted-foreground font-normal cursor-pointer">
                                  <LabelWithTip tip={`CMS case minimum: ${cm.cases} ${cm.unit}. Also requires the reporting entity (group / virtual group / APM Entity) to have at least ${cm.clinicianFloor} clinicians. Toggle on when you expect CMS to score this measure.`}>
                                    Met case minimum ({cm.cases} {id === '479' ? 'admissions' : 'patients'})
                                  </LabelWithTip>
                                </Label>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Changes save automatically. Submission always wins when a file row exists. Blank points = not scoreable.
                  </p>
                </>
              )}
            </div>

            <div className="flex-shrink-0 lg:pl-6 lg:border-l border-gray-200">
              <div className="text-center min-w-[100px]">
                <div className="text-sm text-gray-500 mb-1">{sourceLabel}</div>
                <div className="space-y-1">
                  <div className="text-2xl font-bold text-gray-900 tabular-nums leading-tight">
                    {fmtPts(effective.m479?.points)}
                    <span className="text-sm font-normal text-gray-400 ml-1">479</span>
                  </div>
                  <div className="text-2xl font-bold text-gray-900 tabular-nums leading-tight">
                    {fmtPts(effective.m484?.points)}
                    <span className="text-sm font-normal text-gray-400 ml-1">484</span>
                  </div>
                </div>
                <div className="text-xs text-gray-500 mt-2">Points in use</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
}
